import React, { useMemo, useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  selectDebouncedSearchQuery,
  selectGroupingConfig,
  selectPagination,
  selectApiData,
  selectBacklogViewType,
} from "@/features/bughurd/boardSelector";
import SprintsCalendarView from "./SprintsCalendarView";
import { useParams } from "react-router-dom";
import { useBoardFilters } from "../custom-hooks/useBoardFilters";
import { useDragAndDrop } from "../custom-hooks/useDragAndDropTask";
import { useBulkMove } from "../custom-hooks/useBulkMove";
import {
  useTheme,
  Menu,
  MenuItem,
  Box,
  Typography,
  Skeleton,
  Paper,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TaskUpdateDrawer from "../../bugherdComponents/TaskUpdateDrawer";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import TaskAccordion from "../TaskAccordion";
import {
  useUpdateBugDetailMutation,
  useDeleteBugMutation,
  useCreateSprintMutation,
  useCreateBugMutation,
  useLazyGetListOfBugsQuery,
  useGetSprintListQuery,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
  useUpdateSprintStatusMutation,
} from "@/features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import SprintStartModal from "./SprintStartModal";
import SprintCompleteModal from "./SprintCompleteModal";
import {
  setActiveSprintIds,
  setActiveSprints,
  setBacklogViewType,
  setSelectedItem,
  setViewType,
} from "@/features/bughurd/slice";

// Drag and Drop imports
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import BacklogMenus from "../BacklogMenus";
import SprintWorkloadView from "./SprintWorkload/SprintWorkload";

export interface Sprint {
  _id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  goal?: string;
  sprintKey?: string;
  description?: string;
  totalBugCount?: number;
  doneBugCount?: number;
}

const SprintsTab = () => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  // Redux selectors
  const { statusList } = useAppSelector(selectApiData);

  const { recordsPerPage } = useAppSelector((state) => state.appGlobal);
  const boardState = useAppSelector((state) => state.board);
  const { filters } = useBoardFilters();
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);

  // Extract filters
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
  } = filters;

  // Local state for modals and menus
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [actionTaskId, setActionTaskId] = useState<string | null>(null);
  const [isDeleteSprintModalOpen, setIsDeleteSprintModalOpen] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState<Sprint | null>(null);
  const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);
  const [editingStatusTaskId, setEditingStatusTaskId] = useState<string | null>(
    null
  );
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedSprintForEdit, setSelectedSprintForEdit] =
    useState<Sprint | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>({});

  // Create task states
  const [isCreatingTask, setIsCreatingTask] = useState<{
    [key: string]: boolean;
  }>({});
  const [newTaskNames, setNewTaskNames] = useState<{ [key: string]: string }>(
    {}
  );

  // Sprint task data state
  const [sprintTasksData, setSprintTasksData] = useState<{
    [key: string]: any;
  }>({});

  const [sprintPaginationModels, setSprintPaginationModels] = useState<{
    [key: string]: { page: number; pageSize: number };
  }>({});
  const [isSprintStartModalOpen, setIsSprintStartModalOpen] = useState(false);
  const [selectedSprintForStart, setSelectedSprintForStart] =
    useState<Sprint | null>(null);
  const [sprintMenuAnchorEl, setSprintMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isSprintCompleteModalOpen, setIsSprintCompleteModalOpen] =
    useState(false);
  const [selectedSprintForComplete, setSelectedSprintForComplete] =
    useState<Sprint | null>(null);

  // Row selection states
  const [containerSelections, setContainerSelections] = useState<{
    [key: string]: Set<string>;
  }>({});

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Filter payload that can be reused
  const filterPayload = useMemo(
    () => ({
      search: debouncedSearchQuery || "",
      assignTo: selectedAvatars?.length > 0 ? selectedAvatars : undefined,
      tags: selectedTags?.length > 0 ? selectedTags : undefined,
      statuses: selectedStatuses?.length > 0 ? selectedStatuses : undefined,
      severities:
        selectedPriorities?.length > 0 ? selectedPriorities : undefined,
      ...(dueDateStart && { dueDateStart }),
      ...(dueDateEnd && { dueDateEnd }),
      ...(startDateStart && { startDateStart }),
      ...(startDateEnd && { startDateEnd }),
      ...(updatedAtStart && { updatedAtStart }),
      ...(updatedAtEnd && { updatedAtEnd }),
    }),
    [
      debouncedSearchQuery,
      selectedAvatars,
      selectedTags,
      selectedStatuses,
      selectedPriorities,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      startDateEnd,
      updatedAtStart,
      updatedAtEnd,
    ]
  );

  const {
    data: sprintListData,
    isLoading: isSprintLoading,
    refetch: refetchSprints,
  } = useGetSprintListQuery(
    {
      projectId: id || "",
      page: 1,
      limit: 100,
    },
    {
      skip: !id,
    }
  );

  // Lazy query for sprint tasks
  const [getSprintTasks] = useLazyGetListOfBugsQuery();

  // Additional API hooks for mutations
  const { data: users } = useGetCompanyMembersQuery(1);
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();
  const [createSprint, { isLoading: isCreatingSprint }] =
    useCreateSprintMutation();
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const [updateSprint, { isLoading: isUpdatingSprint }] =
    useUpdateSprintMutation();
  const [updateSprintStatus, { isLoading: isUpdatingSprintStatus }] =
    useUpdateSprintStatusMutation();
  const [deleteSprint, { isLoading: isDeletingSprint }] =
    useDeleteSprintMutation();

  const sprints = sprintListData?.data?.decryptedData?.items || [];

  // Initialize drag and drop hook
  const {
    activeTask,
    isDragging,
    draggedTasks,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    findTask,
    getActiveContainer,
    getAllSelectedTasks,
    clearAllSelections,
  } = useDragAndDrop({
    backlogData: null, // No backlog data in sprints tab
    sprintTasksData,
    setSprintTasksData,
    updateBugMutation: updateBug,
    updateSprintStatusMutation: updateSprintStatus,
    refetchBugList: () => {}, // No backlog to refetch
    sprints,
    containerSelections,
    setContainerSelections,
  });

  // Initialize bulk move hook
  const {
    moveToMenuAnchorEl,
    setMoveToMenuAnchorEl,
    getAvailableMoveDestinations,
    handleBulkMove,
  } = useBulkMove({
    sprints,
    containerSelections,
    setContainerSelections,
    sprintTasksData,
    setSprintTasksData,
    updateSprintStatusMutation: updateSprintStatus,
    refetchBugList: () => {}, // No backlog to refetch
    findTask,
    getActiveContainer,
  });

  const handleSelectionChange =
    (containerId: string) => (selection: Set<string>) => {
      setContainerSelections((prev) => {
        const updated = { ...prev };
        if (selection.size === 0) {
          delete updated[containerId];
        } else {
          updated[containerId] = selection;
        }
        return updated;
      });
    };

  // Get total selected tasks count
  const totalSelectedTasks = getAllSelectedTasks().size;

  // Get active container (container with selections)
  const activeContainer = getActiveContainer();

  useEffect(() => {
    if (sprints.length > 0) {
      const activeSprints = getActiveSprints(sprints);
      dispatch(
        setActiveSprintIds(activeSprints.map((sprint: Sprint) => sprint._id))
      );
      dispatch(setActiveSprints(activeSprints));
    }
  }, [sprints]);

  useEffect(() => {
    if (sprints.length > 0) {
      // Create expanded state for all sprints
      const expandedState: { [key: string]: boolean } = {};

      sprints.forEach((sprint: Sprint) => {
        const sprintKey = `sprint-${sprint._id}`;
        expandedState[sprintKey] = true;
      });

      setExpandedAccordions(expandedState);

      // Load tasks for all sprints
      sprints.forEach(async (sprint: Sprint) => {
        if (!sprintTasksData[sprint._id]) {
          try {
            const response = await getSprintTasks({
              id: id || "",
              forBoard: true,
              sprint: sprint._id,
              ...filterPayload,
              page: 1,
              limit: 100,
            }).unwrap();
            setSprintTasksData((prev) => ({
              ...prev,
              [sprint._id]: response,
            }));
          } catch (error) {
            console.error("Error loading sprint tasks:", error);
          }
        }
      });
    }
  }, [sprints.length, id, filterPayload]);

  // UseEffect to reload sprint tasks when filters change
  useEffect(() => {
    if (sprints.length > 0) {
      sprints.forEach(async (sprint: Sprint) => {
        if (sprintTasksData[sprint._id]) {
          try {
            const response = await getSprintTasks({
              id: id || "",
              forBoard: true,
              sprint: sprint._id,
              ...filterPayload,
              page: 1,
              limit: 100,
            }).unwrap();

            setSprintTasksData((prev) => ({
              ...prev,
              [sprint._id]: response,
            }));
          } catch (error) {
            console.error("Error reloading sprint tasks:", error);
          }
        }
      });
    }
  }, [filterPayload]);

  const handleStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => {
    event.stopPropagation();
    setEditingStatusTaskId(taskId);
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setEditingStatusTaskId(null);
    setStatusAnchorEl(null);
  };

  const handleStatusUpdate = async (newStatusId: string) => {
    if (!editingStatusTaskId) return;

    try {
      const payload = {
        bugId: editingStatusTaskId,
        bugStatus: newStatusId,
      };
      handleStatusClose();

      await updateBug(payload).unwrap();
      toast.success(t("Status updated successfully"));

      // Refresh sprint data
      Object.keys(sprintTasksData).forEach(async (sprintId) => {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();
        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t("Failed to update status"));
      handleStatusClose();
    }
  };

  const handleCompleteSprint = async (
    event: React.MouseEvent,
    sprint: Sprint
  ) => {
    event.stopPropagation();
    setSelectedSprintForComplete(sprint);
    setIsSprintCompleteModalOpen(true);
  };

  const handleSprintComplete = async (data: any) => {
    try {
      const payload: any = {
        status: "ARCHIVE",
        sprintId: data.sprintId,
      };
      if (data.moveOpenItemsTo == "backlog") {
        payload.moveItems = "BACKLOG";
      } else if (data.moveOpenItemsTo == "new_sprint") {
        payload.moveItems = "NEW_SPRINT";
      } else {
        payload.moveItems = "EXISTING_SPRINT";
        payload.sprint = data.moveOpenItemsTo;
      }
      await updateSprintStatus(payload).unwrap();
      toast.success(t("Sprint completed successfully"));
      setIsSprintCompleteModalOpen(false);
      setSelectedSprintForComplete(null);
      await refetchSprints();

      if (
        data.moveOpenItemsTo !== "new_sprint" &&
        data.moveOpenItemsTo !== "backlog"
      ) {
        const targetSprintId = data.moveOpenItemsTo;
        try {
          const response = await getSprintTasks({
            id: id || "",
            forBoard: true,
            sprint: targetSprintId,
            page: 1,
            limit: 100,
          }).unwrap();
          setSprintTasksData((prev) => ({
            ...prev,
            [targetSprintId]: response,
          }));
        } catch (error) {
          console.error("Error refreshing target sprint tasks:", error);
        }
      }
    } catch (error) {
      console.error("Error completing sprint:", error);
      toast.error(t("Failed to complete sprint"));
    }
  };

  const handleCloseSprintCompleteModal = () => {
    setIsSprintCompleteModalOpen(false);
    setSelectedSprintForComplete(null);
  };

  const openDeleteSprintModal = (sprintId: string) => {
    const sprint = sprints.find((s: Sprint) => s._id === sprintId);
    if (sprint) {
      setSprintToDelete(sprint);
      setIsDeleteSprintModalOpen(true);
    }
  };

  const closeDeleteSprintModal = () => {
    setIsDeleteSprintModalOpen(false);
    setSprintToDelete(null);
  };

  const handleConfirmDeleteSprint = async () => {
    if (!sprintToDelete) return;

    try {
      await deleteSprint(sprintToDelete._id).unwrap();
      toast.success(t("Sprint deleted successfully"));

      // Refresh sprints list
      await refetchSprints();

      // Remove sprint tasks data from local state
      setSprintTasksData((prev) => {
        const updated = { ...prev };
        delete updated[sprintToDelete._id];
        return updated;
      });

      // Close the modal
      closeDeleteSprintModal();
    } catch (error) {
      console.error("Error deleting sprint:", error);
      toast.error(t("Failed to delete sprint"));
      closeDeleteSprintModal();
    }
  };

  const getDraftSprints = () => {
    return sprints.filter((sprint: Sprint) => sprint.status === "DRAFT");
  };

  const getActiveSprints = (sprints: Sprint[]) => {
    return sprints.filter((sprint: Sprint) => sprint.status === "ACTIVE");
  };

  const getAvailableSprints = () => {
    return sprints.filter(
      (sprint: Sprint) =>
        sprint.status === "DRAFT" || sprint.status === "ACTIVE"
    );
  };

  const loadSprintTasks = async (sprintId: string) => {
    if (sprintTasksData[sprintId]) return;

    try {
      const response = await getSprintTasks({
        id: id || "",
        forBoard: true,
        sprint: sprintId,
        ...filterPayload,
        page: 1,
        limit: 100,
      }).unwrap();
      setSprintTasksData((prev) => ({
        ...prev,
        [sprintId]: response,
      }));
    } catch (error) {
      console.error("Error loading sprint tasks:", error);
    }
  };

  const handleSprintPaginationChange =
    (sprintId: string) => (newModel: { page: number; pageSize: number }) => {
      setSprintPaginationModels((prev) => ({
        ...prev,
        [sprintId]: newModel,
      }));

      loadSprintTasksWithPagination(sprintId, newModel.page, newModel.pageSize);
    };

  const loadSprintTasksWithPagination = async (
    sprintId: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const response = await getSprintTasks({
        id: id || "",
        forBoard: true,
        sprint: sprintId,
        ...filterPayload,
        page: page,
        limit: pageSize,
      }).unwrap();
      setSprintTasksData((prev) => ({
        ...prev,
        [sprintId]: response,
      }));
    } catch (error) {
      console.error("Error loading sprint tasks:", error);
    }
  };

  const getTodoStatusId = () => {
    const todoStatus = statusList?.find(
      (status: any) => status.statusCode === "TODO"
    );
    return todoStatus?._id || "";
  };

  const handleCreateTaskClick = (containerKey: string) => {
    setIsCreatingTask((prev) => ({ ...prev, [containerKey]: true }));
    setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));

    setTimeout(() => {
      const textField = document.querySelector(
        `input[data-testid="create-task-${containerKey}"]`
      ) as HTMLInputElement;
      if (textField) {
        textField.focus();
      }
    }, 0);
  };

  const handleCreateTask = async (containerKey: string, sprintId: string) => {
    const taskName = newTaskNames[containerKey];
    if (!taskName?.trim()) {
      setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
      setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
      return;
    }

    if (!id) {
      toast.error(t("Project ID is required"));
      return;
    }

    const todoStatusId = getTodoStatusId();
    if (!todoStatusId) {
      toast.error(t("TODO status not found"));
      return;
    }

    try {
      const payload: any = {
        summary: taskName.trim(),
        type: "STORY",
        project: id,
        bugStatus: todoStatusId,
        severity: "Medium",
        currentSprint: sprintId,
      };

      await createBug(payload).unwrap();
      toast.success(t("Task created successfully"));

      const response = await getSprintTasks({
        id: id || "",
        forBoard: true,
        sprint: sprintId,
        ...filterPayload,
        page: 1,
        limit: 100,
      }).unwrap();
      setSprintTasksData((prev) => ({
        ...prev,
        [sprintId]: response,
      }));

      setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
      setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error(t("Failed to create task"));
    }
  };

  const handleCancelTaskCreation = (containerKey: string) => {
    setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
    setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
  };

  const handleKeyPress = (
    event: React.KeyboardEvent,
    containerKey: string,
    sprintId: string
  ) => {
    if (event.key === "Enter") {
      handleCreateTask(containerKey, sprintId);
    } else if (event.key === "Escape") {
      handleCancelTaskCreation(containerKey);
    }
  };

  const handleCreateSprint = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!id) {
      toast.error(t("Project ID is required"));
      return;
    }

    try {
      const payload = {
        project: id,
        name: `Sprint ${sprints.length + 1}`,
        status: "DRAFT",
      };

      await createSprint(payload).unwrap();
      toast.success(t("Sprint created successfully"));
      refetchSprints();
    } catch (error) {
      console.error("Error creating sprint:", error);
      toast.error(t("Failed to create sprint"));
    }
  };

  const handleStartSprint = async (event: React.MouseEvent, sprint: Sprint) => {
    event.stopPropagation();

    const sprintTasks =
      sprintTasksData[sprint._id]?.data?.decryptedData?.items || [];
    if (sprintTasks.length === 0) {
      toast.error(t("Cannot start sprint without tasks"));
      return;
    }

    setSelectedSprintForStart(sprint);
    setIsSprintStartModalOpen(true);
  };

  const handleSprintStart = async (data: any) => {
    try {
      const payload = {
        project: id,
        name: data.name,
        status: "ACTIVE",
        startDate: data.startDate,
        endDate: data.endDate,
        sprintId: data.sprintId,
        description: data.goal,
        duration: data.duration,
      };
      await updateSprint(payload).unwrap();
      toast.success(t("Sprint started successfully"));
      setIsSprintStartModalOpen(false);
      setSelectedSprintForStart(null);
      refetchSprints();
      dispatch(setSelectedItem("All Sprints"));
      dispatch(setBacklogViewType("allSprint"));
      dispatch(setViewType("allSprint"));
    } catch (error) {
      console.error("Error starting sprint:", error);
      toast.error(t("Failed to start sprint"));
    }
  };

  const handleCloseSprintStartModal = () => {
    setIsSprintStartModalOpen(false);
    setSelectedSprintForStart(null);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
    setActionTaskId(rowId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSprintMenuAnchorEl(null);
    setSelectedRowId(null);
    setActionTaskId(null);
  };

  const handleEditTask = (taskId: string) => {
    handleMenuClose();
    setSelectedTaskId(taskId);
    setIsTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
  };

  const openDeleteModal = (taskId: string) => {
    handleMenuClose();
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const handleEditSprint = async (sprintId: string) => {
    const sprint = sprints.find((s: Sprint) => s._id === sprintId);
    if (sprint) {
      setSelectedSprintForEdit(sprint);
      setIsEditSprintModalOpen(true);
    }
  };

  const handleCloseEditSprintModal = () => {
    setIsEditSprintModalOpen(false);
    setSelectedSprintForEdit(null);
  };

  const handleSprintUpdate = async (data: any) => {
    try {
      const payload = {
        project: id,
        name: data.name,
        status: selectedSprintForEdit?.status === "DRAFT" ? "DRAFT" : "ACTIVE",
        startDate: data.startDate,
        endDate: data.endDate,
        sprintId: data.sprintId,
        description: data.goal,
        duration: data.duration,
      };

      await updateSprint(payload).unwrap();
      toast.success(t("Sprint updated successfully"));
      setIsEditSprintModalOpen(false);
      setSelectedSprintForEdit(null);
      refetchSprints();
    } catch (error) {
      console.error("Error updating sprint:", error);
      toast.error(t("Failed to update sprint"));
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await deleteBug(taskToDelete).unwrap();
      toast.success(t("Task deleted successfully"));

      // Refresh all sprint tasks data
      Object.keys(sprintTasksData).forEach(async (sprintId) => {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();
        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      });
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(t("Failed to delete task"));
      closeDeleteModal();
    }
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [panel]: isExpanded,
      }));

      if (isExpanded && panel.startsWith("sprint-")) {
        const sprintId = panel.replace("sprint-", "");
        if (!sprintTasksData[sprintId]) {
          loadSprintTasks(sprintId);
        }
      }
    };

  const selectedTask = findTask(selectedTaskId || "");

  const isGlobalLoading = () => {
    return isDeleting || isUpdating;
  };

  // Group sprints by status
  const draftSprints = sprints.filter(
    (sprint: Sprint) => sprint.status === "DRAFT"
  );
  const activeSprints = sprints.filter(
    (sprint: Sprint) => sprint.status === "ACTIVE"
  );
  const archivedSprints = sprints.filter(
    (sprint: Sprint) => sprint.status === "ARCHIVE"
  );

  // Render Sprint Accordions Component
  const renderSprintAccordions = (
    sprintsToRender: Sprint[],
    sectionTitle: string
  ) => {
    if (sprintsToRender.length === 0) {
      return null;
    }
    // return <SprintWorkloadView />;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "text.secondary",
            px: 1,
          }}
        >
          {sectionTitle} ({sprintsToRender.length})
        </Typography>

        {sprintsToRender.map((sprint: Sprint) => {
          const sprintKey = `sprint-${sprint._id}`;
          const droppableId = sprintKey;
          const isExpanded = expandedAccordions[sprintKey] || false;
          const sprintTasks =
            sprintTasksData[sprint._id]?.data?.decryptedData?.items || [];
          const sprintTaskCount =
            sprintTasksData[sprint._id]?.data?.decryptedData?.total || 0;
          const isSprintTasksLoading =
            isExpanded && !sprintTasksData[sprint._id];
          const sprintPagination = sprintPaginationModels[sprint._id] || {
            page: 1,
            pageSize: 10,
          };

          return (
            <TaskAccordion
              key={sprint._id}
              accordionKey={sprintKey}
              droppableId={droppableId}
              title={sprint.name}
              isExpanded={isExpanded}
              onAccordionChange={handleAccordionChange(sprintKey)}
              tasks={sprintTasks}
              totalCount={sprintTaskCount}
              isLoading={isSprintTasksLoading || isUpdating}
              paginationModel={sprintPagination}
              setPaginationModel={handleSprintPaginationChange(sprint._id)}
              onTaskClick={handleEditTask}
              onMenuClick={handleMenuClick}
              isCreatingTask={isCreatingTask[sprintKey] || false}
              newTaskName={newTaskNames[sprintKey] || ""}
              onCreateTaskClick={() => handleCreateTaskClick(sprintKey)}
              onTaskNameChange={(name) =>
                setNewTaskNames((prev) => ({
                  ...prev,
                  [sprintKey]: name,
                }))
              }
              onCreateTask={() => handleCreateTask(sprintKey, sprint._id)}
              onCancelTaskCreation={() => handleCancelTaskCreation(sprintKey)}
              onKeyPress={(e) => handleKeyPress(e, sprintKey, sprint._id)}
              isCreatingBug={isCreatingBug}
              onDeleteSprint={openDeleteSprintModal}
              onEditSprint={handleEditSprint}
              isDeletingSprint={isDeletingSprint}
              headerButtons={sprint?.status === "ARCHIVE" ? [] : [
                {
                  label:
                    sprint.status === "DRAFT"
                      ? t("Start Sprint")
                      : sprint.status === "ACTIVE"
                      ? t("Complete Sprint")
                      : undefined,
                  icon:
                    sprint.status === "DRAFT" ? (
                      <PlayArrowIcon />
                    ) : sprint.status === "ACTIVE" ? (
                      <AddIcon />
                    ) : undefined,
                  onClick: (e) => {
                    if (sprint.status === "DRAFT") {
                      handleStartSprint(e, sprint);
                    } else if (sprint.status === "ACTIVE") {
                      handleCompleteSprint(e, sprint);
                    }
                  },
                  disabled:
                    sprint.status === "DRAFT" && sprintTasks.length === 0,
                },
              ]}
              sprint={sprint}
              emptyMessage={t("No tasks found in this sprint")}
              isDragging={isDragging}
              selectedTasks={containerSelections[sprintKey] || new Set()}
              onSelectionChange={handleSelectionChange(sprintKey)}
              isSelectionDisabled={
                activeContainer !== null && activeContainer !== sprintKey
              }
              onStatusClick={handleStatusClick}
              editingStatusTaskId={editingStatusTaskId}
              statusList={statusList}
              isUpdatingStatus={isUpdating}
            />
          );
        })}
      </Box>
    );
  };

  const renderContent = () => {
    if (isSprintLoading) {
      return (
        <Box sx={{ mb: 2 }}>
          {[...Array(3)].map((_, index) => (
            <Paper key={index} sx={{ mb: 2, p: 2 }}>
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{ mt: 1 }}
              />
            </Paper>
          ))}
        </Box>
      );
    }

    if (sprints.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {t("No sprints found")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("Create your first sprint to start organizing your tasks")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSprint}
            disabled={isCreatingSprint}
          >
            {isCreatingSprint ? t("Creating...") : t("Create Sprint")}
          </Button>
        </Box>
      );
    }

    return (
      <>
        {/* Create Sprint Button */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSprint}
            disabled={isCreatingSprint}
          >
            {isCreatingSprint ? t("Creating...") : t("Create Sprint")}
          </Button>
        </Box>

        {/* Render sprints grouped by status */}
        {renderSprintAccordions(activeSprints, t("Active Sprints"))}
        {renderSprintAccordions(draftSprints, t("Draft Sprints"))}
        {renderSprintAccordions(archivedSprints, t("Completed Sprints"))}
        {/* <SprintWorkloadView /> */}
      </>
    );
  };

  if (boardState.backlogViewType === "calendar") {
    return (
      <SprintsCalendarView
        sprints={sprints}
        tasks={sprintTasksData || []}
        onSprintClick={(sprint) => {
          // Example: Open edit modal
          setIsEditSprintModalOpen(true);
          setSelectedSprintForEdit(sprint);
        }}
        onTaskClick={(task) => {
          setSelectedTaskId(task._id);
          // setIsTaskModalOpen(true);
        }}
        onDateClick={(date) => {}}
        onPlusClick={(date) => {}}
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          height: "calc(100vh - 250px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header with selection info */}
        {totalSelectedTasks > 0 && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body1">
              {t("{{count}} task(s) selected", { count: totalSelectedTasks })}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setMoveToMenuAnchorEl(e.currentTarget)}
                disabled={getAvailableMoveDestinations().length === 0}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.contrastText,
                }}
              >
                {t("Move to")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={clearAllSelections}
                sx={{
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.contrastText,
                }}
              >
                {t("Clear Selection")}
              </Button>
            </Box>
          </Box>
        )}

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            paddingRight: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                background: theme.palette.grey[600],
              },
            },
          }}
        >
          {renderContent()}
        </Box>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeTask && draggedTasks.length > 0 ? (
            <Paper
              sx={{
                p: 2,
                minWidth: 300,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[8],
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
                opacity: 0.9,
              }}
            >
              {draggedTasks.length === 1 ? (
                <Typography variant="body2" fontWeight={500}>
                  {activeTask.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {activeTask.bugKey}:
                    </span>
                  )}
                  {activeTask.summary}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    {t("Moving {{count}} tasks", {
                      count: draggedTasks.length,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeTask.bugKey && (
                      <span style={{ marginRight: "4px", color: "#1976D2" }}>
                        {activeTask.bugKey}:
                      </span>
                    )}
                    {activeTask.summary}
                    {draggedTasks.length > 1 && " + others..."}
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : null}
        </DragOverlay>

        {/* Context Menus */}
        <BacklogMenus
          menuAnchorEl={menuAnchorEl}
          onMenuClose={handleMenuClose}
          actionTaskId={actionTaskId}
          onEditTask={handleEditTask}
          onDeleteTask={openDeleteModal}
          sprintMenuAnchorEl={sprintMenuAnchorEl}
          onSprintMenuClose={() => setSprintMenuAnchorEl(null)}
          onSprintMenuOpen={(e) => setSprintMenuAnchorEl(e.currentTarget)}
          availableSprints={getAvailableSprints()}
          onMoveToSprint={() => {}}
          isBacklogTask={() => false}
          isUpdating={isUpdating}
          moveToMenuAnchorEl={moveToMenuAnchorEl}
          onMoveToMenuClose={() => setMoveToMenuAnchorEl(null)}
          availableMoveDestinations={getAvailableMoveDestinations()}
          onBulkMove={handleBulkMove}
          isUpdatingSprintStatus={isUpdatingSprintStatus}
          sprints={sprints}
        />

        {/* Status Selection Menu */}
        <Menu
          anchorEl={statusAnchorEl}
          open={Boolean(statusAnchorEl)}
          onClose={handleStatusClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              minWidth: 160,
              maxHeight: 300,
            },
          }}
        >
          {statusList?.map((status: any) => (
            <MenuItem
              key={status._id}
              onClick={() => handleStatusUpdate(status._id)}
              disabled={isUpdating}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 1,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {status.status}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Task Update Drawer */}
        {selectedTaskId && isTaskModalOpen && (
          <TaskUpdateDrawer
            open={isTaskModalOpen}
            onClose={handleCloseTaskModal}
            setBugId={() => {}}
            setDrawerOpen={() => {}}
            bugId={selectedTaskId}
            users={users?.data?.decryptedData || []}
            createdBy={selectedTask?.createdBy?.fullName || ""}
            setCreatedBy={() => {}}
            refreshColumn={() => {
              Object.keys(sprintTasksData).forEach(async (sprintId) => {
                const response = await getSprintTasks({
                  id: id || "",
                  forBoard: true,
                  sprint: sprintId,
                  ...filterPayload,
                  page: 1,
                  limit: 100,
                }).unwrap();
                setSprintTasksData((prev) => ({
                  ...prev,
                  [sprintId]: response,
                }));
              });
            }}
          />
        )}

        {/* Task Delete Confirmation Modal */}
        <DeleteConfirmModal
          open={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteTask}
          loading={isDeleting}
          title={t("Delete Task")}
          confirmMessage={t("Are you sure you want to delete this task?")}
          okText={t("Delete")}
        />

        {/* Sprint Delete Confirmation Modal */}
        <DeleteConfirmModal
          open={isDeleteSprintModalOpen}
          onClose={closeDeleteSprintModal}
          onConfirm={handleConfirmDeleteSprint}
          loading={isDeletingSprint}
          title={t("Delete Sprint")}
          confirmMessage={
            sprintToDelete
              ? t(
                  "Are you sure you want to delete sprint 'test'? This action cannot be undone.",
                  {
                    sprintName: sprintToDelete.name,
                  }
                )
              : t("Are you sure you want to delete this sprint?")
          }
          okText={t("Delete")}
        />

        {/* Sprint Start Modal */}
        {selectedSprintForStart && (
          <SprintStartModal
            open={isSprintStartModalOpen}
            onClose={handleCloseSprintStartModal}
            onStart={handleSprintStart}
            sprint={selectedSprintForStart}
            taskCount={
              sprintTasksData[selectedSprintForStart._id]?.data?.decryptedData
                ?.total || 0
            }
            isLoading={isUpdatingSprint}
          />
        )}

        {/* Sprint Edit Modal */}
        {selectedSprintForEdit && (
          <SprintStartModal
            open={isEditSprintModalOpen}
            onClose={handleCloseEditSprintModal}
            onStart={handleSprintUpdate}
            sprint={selectedSprintForEdit}
            taskCount={
              sprintTasksData[selectedSprintForEdit._id]?.data?.decryptedData
                ?.total || 0
            }
            isLoading={isUpdatingSprint}
            isEditMode={true}
          />
        )}

        {/* Sprint Complete Modal */}
        {selectedSprintForComplete && (
          <SprintCompleteModal
            open={isSprintCompleteModalOpen}
            onClose={handleCloseSprintCompleteModal}
            onComplete={handleSprintComplete}
            sprint={selectedSprintForComplete?._id}
            draftSprints={getDraftSprints()}
            isLoading={isUpdatingSprintStatus}
          />
        )}
      </Box>
    </DndContext>
  );
};

export default SprintsTab;
